/**
 * <LiveEngagementScoreCard> — the "Score d'engagement" block (organism, L0.1).
 *
 * The card that mirrors the legacy engagement block: the big `total /100`, then either the points
 * d'attention (`<LiveEngagementScoreAlerts>`) or a "Séance bien engagée" `<Banner>`, then the three
 * axes + bar (`<LiveEngagementScoreList>`). Pure composition — it only assembles focused molecules
 * and passes `scores` down; no layout logic of its own beyond the header. No hex.
 */
import React from "react"

import { Card } from "../../molecules/Card"
import { Stack } from "../../atoms/containers/Stack"
import { Row } from "../../atoms/containers/Row"
import { Title } from "../../atoms/typography/Title"
import { Text } from "../../atoms/typography/Text"
import { Banner } from "../../atoms/Banner"
import { LiveEngagementScoreAlerts } from "../../molecules/LiveEngagementScoreAlerts"
import { LiveEngagementScoreList } from "../../molecules/LiveEngagementScoreList"
import { type UILiveSessionReportDetails } from "../../../ports/primary/ui/types"
import { showEngagementOk } from "../../../ports/primary/ui/formatters/liveSessionReportFormatter"

export type LiveEngagementScoreCardProps = {
    scores: NonNullable<UILiveSessionReportDetails["scores"]>
    testID?: string
}

export function LiveEngagementScoreCard(props: LiveEngagementScoreCardProps) {
    const { scores, testID } = props

    return (
        <Card p="5" testID={testID}>
            <Stack gap="3">
                <Title center size="text-xl" uppercase>
                    Score d&apos;engagement sur la session
                </Title>

                <Row justify="center" align="baseline" gap="1">
                    <Text size="text-4xl" weight="extrabold">
                        {scores.total}
                    </Text>
                    <Text size="text-2xl" weight="bold" color="text-muted">
                        /100
                    </Text>
                </Row>

                {scores.alerts.length > 0 ? (
                    <LiveEngagementScoreAlerts alerts={scores.alerts} />
                ) : showEngagementOk(scores) ? (
                    <Banner icon="check-circle" color="success" message="Séance bien engagée" tinted />
                ) : null}

                <LiveEngagementScoreList scores={scores} />
            </Stack>
        </Card>
    )
}
